/**
 * 🏆 Leaderboards — Mar 3, 2026 owner drop.
 *
 * Five tabs, all pulling from the new global leaderboard endpoints:
 *   💕 Hearts         — /api/leaderboard/hearts
 *   🎖️ Personal EXP  — /api/leaderboard/personal-exp
 *   🐦‍🔥 Pet Level   — /api/leaderboard/pet-level
 *   🌈 Evolution Lv  — /api/leaderboard/evolution
 *   🏆 Friends       — /api/friends/leaderboard (existing)
 *
 * Global boards return `{ top: [...], me: {...}, count }`. `me` row is
 * pinned separately at the top so you always see yourself, even when
 * you're not in the top 50. Owner + NPCs are excluded from the podium
 * server-side.
 *
 * Style follows the app's austere gold-on-black identity — no purple
 * gradients, no rainbow slop, kept high-contrast with a single accent.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "aether-ash",   label: "⚡ The Ladder",   endpoint: "/leaderboard/aether-ash",
    accent: "#FBBF24", scoreKey: "total_power",     scoreSuffix: "pwr",
    scoreLabel: "Total Power",
    describe: (r) => {
      // The Aether / Ash split now lives in the leaderboard row (per
      // owner directive Feb 2026). Hero page shows one BP number only.
      const base = r.tier ? `${r.tier} · Lv ${r.hero_level} ${r.hero_class || ""}` : null;
      const hasAether = typeof r.aether_power === "number";
      const hasAsh    = typeof r.ash_power    === "number";
      if (!hasAether && !hasAsh) return base;
      const split = `⚡ Aether ${Number(r.aether_power||0).toLocaleString()} · 🔥 Ash ${Number(r.ash_power||0).toLocaleString()}`;
      return base ? `${base} · ${split}` : split;
    } },
  { key: "hearts",       label: "💕 Hearts",       endpoint: "/leaderboard/hearts",
    accent: "#F472B6", scoreKey: "hearts_total",     scoreSuffix: "💕",
    scoreLabel: "Heart Jar",
    describe: (r) => null },
  { key: "personal-exp", label: "🎖️ Personal EXP", endpoint: "/leaderboard/personal-exp",
    accent: "#D4AF37", scoreKey: "hero_level",       scoreSuffix: "",
    scoreLabel: "Hero Lv",
    describe: (r) => r.hero_class_id ? `${r.hero_class_id.replace(/_/g, " ")} · ${r.hero_xp.toLocaleString()} xp` : null },
  { key: "pet-level",    label: "🐦‍🔥 Pet Level",  endpoint: "/leaderboard/pet-level",
    accent: "#FDBA74", scoreKey: "pet_level",        scoreSuffix: "",
    scoreLabel: "Pet Lv",
    describe: (r) => r.pet_name ? `${r.pet_name} · ${r.pet_level_xp.toLocaleString()} xp · ${r.pet_tier}` : null },
  { key: "evolution",    label: "🌈 Evolution Lv", endpoint: "/leaderboard/evolution",
    accent: "#A78BFA", scoreKey: "pet_tier_number", scoreSuffix: "",
    scoreLabel: "Spectrum",
    describe: (r) => r.pet_name ? `${r.pet_name} · ${r.pet_tier} · ${r.pet_rebirth} rebirths` : null },
  { key: "friends",      label: "🏆 Friends",       endpoint: "/friends/leaderboard?sort=activity_streak",
    accent: "#22D3EE", scoreKey: "activity_streak", scoreSuffix: "day",
    scoreLabel: "Streak",
    describe: (r) => `${r.workouts_count || 0} workouts · ${r.badges_count || 0} badges`,
    isFriends: true },
];

export default function Leaderboards() {
  const [active, setActive] = useState("aether-ash");
  const [rowsByTab, setRowsByTab] = useState({});
  const [meByTab, setMeByTab] = useState({});
  const [loading, setLoading] = useState(false);
  const tab = TABS.find((t) => t.key === active) || TABS[0];

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (rowsByTab[active] !== undefined) return; // simple client cache
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}${tab.endpoint}`);
        if (cancelled) return;
        if (tab.isFriends) {
          setRowsByTab((r) => ({ ...r, [active]: data.rows || [] }));
          setMeByTab((m) => ({ ...m, [active]: null }));
        } else {
          setRowsByTab((r) => ({ ...r, [active]: data.top || [] }));
          setMeByTab((m) => ({ ...m, [active]: data.me || null }));
        }
      } catch (e) {
        if (e?.response?.status === 401) {
          toast.error("Please sign in to see leaderboards.");
        } else {
          toast.error(e?.response?.data?.detail || "Couldn't load leaderboard.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const rows = rowsByTab[active] || [];
  const me = meByTab[active];

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="leaderboards-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 pt-8 pb-4">
        <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-2">
          🏆 Leaderboards
        </div>
        <h1 className="font-display text-3xl sm:text-4xl leading-none">
          Who's flying <span style={{ color: tab.accent }}>highest</span>?
        </h1>
        <p className="text-sm text-neutral-400 mt-2 max-w-lg">
          Live global rankings. Owner + NPC accounts are held off the podium — this is
          the community leaderboard. Your row is always pinned so you see where you stand.
        </p>
      </div>

      {/* Tab strip */}
      <div className="max-w-3xl mx-auto px-4 overflow-x-auto">
        <div className="flex gap-1.5 pb-3 min-w-max">
          {TABS.map((t) => {
            const isActive = t.key === active;
            return (
              <button
                key={t.key}
                data-testid={`lb-tab-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`text-[11px] uppercase tracking-[0.25em] font-bold px-3 py-2 border transition ${
                  isActive
                    ? "text-black border-transparent"
                    : "text-neutral-400 border-zinc-800 hover:text-white hover:border-zinc-600"
                }`}
                style={isActive ? { backgroundColor: t.accent, borderColor: t.accent } : {}}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4">
        {/* Pinned "me" row (global boards only) */}
        {me && !tab.isFriends && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
              Your Rank
            </div>
            <LeaderboardRow row={me} tab={tab} pinned />
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
          {tab.isFriends ? "Your Squad" : "Top of the Board"}
        </div>

        {loading && rows.length === 0 && (
          <div className="text-center text-neutral-500 py-14 text-sm">
            Loading rankings…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-center text-neutral-500 py-14 text-sm">
            No one's on the board yet. Be the first — climb up and plant your flag.
          </div>
        )}
        <div className="space-y-1.5">
          {rows.map((row) => (
            <LeaderboardRow
              key={row.user_id || row.rank}
              row={row}
              tab={tab}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function LeaderboardRow({ row, tab, pinned = false }) {
  const isTop3 = row.rank <= 3 && !pinned;
  const medal = row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : null;
  const score = Number(row[tab.scoreKey] ?? 0);
  const desc = tab.describe(row);
  return (
    <div
      data-testid={`lb-row-${row.rank}`}
      className={`flex items-center gap-3 border p-2.5 ${
        row.is_me
          ? "border-[#D4AF37] bg-[#0F0F0F]"
          : "border-zinc-900 bg-[#0A0A0A] hover:border-zinc-700 transition"
      }`}
      style={pinned ? { borderColor: tab.accent } : {}}
    >
      {/* Rank */}
      <div
        className="w-9 text-center font-display text-lg leading-none"
        style={isTop3 ? { color: tab.accent } : {}}
      >
        {medal || `#${row.rank}`}
      </div>
      {/* Avatar */}
      <div className="w-10 h-10 shrink-0 bg-zinc-900 flex items-center justify-center overflow-hidden border border-zinc-800">
        {row.picture ? (
          <img src={row.picture} alt={row.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-sm text-neutral-500">{(row.name || "?").slice(0, 1).toUpperCase()}</span>
        )}
      </div>
      {/* Name + description */}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate flex items-center gap-1.5">
          {row.name || "—"}
          {row.is_me && (
            <span className="text-[9px] uppercase tracking-widest text-[#D4AF37]">you</span>
          )}
          {row.role === "owner" && (
            <span className="text-[9px] uppercase tracking-widest text-neutral-500" title="Founder">
              🐦‍🔥
            </span>
          )}
        </div>
        {desc && (
          <div className="text-[11px] text-neutral-500 truncate italic">{desc}</div>
        )}
      </div>
      {/* Score */}
      <div className="text-right shrink-0">
        <div
          className="font-display text-xl leading-none"
          style={{ color: tab.accent }}
        >
          {score.toLocaleString()}{tab.scoreSuffix ? " " + tab.scoreSuffix : ""}
        </div>
        <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-1">
          {tab.scoreLabel}
        </div>
      </div>
    </div>
  );
}
